import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { text } from '../text';
import { hooks } from '../hooks';
import { components } from '../components';
import { theme } from '../constants';
import { useLanguage } from '../context/LanguageContext';
import axiosInstance from '../services/axiosInstance';
import { socketService } from '../services';
import { chatCache } from '../services/chatCacheService';
import { compressImage } from '../utils/imageUtils';
import { AttachmentViewer } from '../components/AttachmentViewer';
import { MdSend, MdAttachFile, MdPerson, MdInfo, MdClose, MdMoreVert, MdSearch, MdDeleteSweep, MdReply, MdEdit, MdDelete, MdBlock } from 'react-icons/md';

const SendIcon = MdSend as any;
const AttachIcon = MdAttachFile as any;
const PersonIcon = MdPerson as any;
const InfoIcon = MdInfo as any;
const CloseIcon = MdClose as any;
const MoreIcon = MdMoreVert as any;
const SearchIcon = MdSearch as any;
const ClearIcon = MdDeleteSweep as any;
const ReplyIcon = MdReply as any;
const EditIcon = MdEdit as any;
const DeleteIcon = MdDelete as any;
const BlockIcon = MdBlock as any;

interface ReplyPreview {
  id: number;
  sender_name: string | null;
  body: string | null;
  attachment_name: string | null;
  deleted: boolean;
  created_at: string;
}

interface ChatMessage {
  id: number;
  conversation_id: number;
  sender_id: number;
  sender_name?: string;
  body: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  reply_to?: ReplyPreview | null;
  edited_at?: string | null;
  deleted_at?: string | null;
  created_at: string;
}

const ALLOWED_ATTACHMENT_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx';
const MAX_ATTACHMENT_BYTES = 7 * 1024 * 1024;

// Tandai percakapan sudah dibaca + kabari BottomTabBar (komponen terpisah, di luar
// tree ChatRoom) supaya badge unread chat langsung diperbarui tanpa perlu pindah tab dulu.
const markReadAndNotify = (conversationId: number) => {
  axiosInstance.patch(`/chat/conversations/${conversationId}/read`)
    .then(() => window.dispatchEvent(new CustomEvent('chat-read')))
    .catch(() => {});
};

const dayKey = (iso: string) => new Date(iso).toDateString();

const formatDateSeparator = (iso: string, todayLabel: string, yesterdayLabel: string) => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(iso) === dayKey(today.toISOString())) return todayLabel;
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return yesterdayLabel;
  return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });
};

export const ChatRoom: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const { state, search } = useLocation();
  const { t } = useLanguage();
  const tc = (t as any).chat;
  const authSlice = hooks.useAppSelector((s: any) => s.authSlice);
  const imageBaseUrl = (process.env.REACT_APP_IMAGE_BASE_URL || '').replace(/\/$/, '');

  // Prioritas: 1. state dari navigasi in-app, 2. query param ?conversationId= (dibuka
  // langsung dari notifikasi push FCM — tidak ada location.state sama sekali saat itu).
  const conversationIdFromUrl = new URLSearchParams(search).get('conversationId');
  const conversationId = state?.conversationId || (conversationIdFromUrl ? parseInt(conversationIdFromUrl, 10) : undefined);
  const [headerTitle, setHeaderTitle] = useState(state?.title || '');
  const [conversationType, setConversationType] = useState<'direct' | 'group'>(state?.type || 'direct');
  const [, setHeaderPhoto] = useState(state?.photo || null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showParticipants, setShowParticipants] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ChatMessage[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null);
  const [jumping, setJumping] = useState(false);
  const [contextMenuMsg, setContextMenuMsg] = useState<ChatMessage | null>(null);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const messageIdsRef = useRef<Set<number>>(new Set());
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressMovedRef = useRef(false);

  const appendIfNew = useCallback((msg: ChatMessage) => {
    if (messageIdsRef.current.has(msg.id)) return;
    messageIdsRef.current.add(msg.id);
    setMessages((prev) => [...prev, msg].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()));
  }, []);

  // Timpa isi pesan yang sudah ada di layar (hasil edit) — kalau belum ada di layar
  // (mis. riwayat lama yang belum di-load), tidak perlu apa-apa, versi terbaru akan
  // otomatis kebawa saat pesan itu di-load/dilompati nanti.
  const upsertMessage = useCallback((msg: ChatMessage) => {
    if (!messageIdsRef.current.has(msg.id)) return;
    setMessages((prev) => prev.map((m) => (m.id === msg.id ? msg : m)));
  }, []);

  const markMessageDeleted = useCallback((messageId: number) => {
    if (!messageIdsRef.current.has(messageId)) return;
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, body: null, attachment_url: null, attachment_name: null, attachment_type: null, deleted_at: new Date().toISOString() } : m)));
  }, []);

  // Load lokal (instan) lalu delta-sync ke server
  useEffect(() => {
    if (!conversationId || !authSlice.userId) return;
    let cancelled = false;

    const load = async () => {
      setLoadingInitial(true);
      const cached = await chatCache.getMessages(authSlice.userId, conversationId);
      if (!cancelled && cached.length) {
        const mapped = cached.map((m) => ({
          id: m.server_id, conversation_id: m.conversation_id, sender_id: m.sender_id, sender_name: m.sender_name || undefined,
          body: m.body, attachment_url: m.attachment_url, attachment_name: m.attachment_name,
          attachment_type: m.attachment_type, reply_to: m.reply_to, edited_at: m.edited_at, deleted_at: m.deleted_at,
          created_at: m.created_at,
        }));
        mapped.forEach((m) => messageIdsRef.current.add(m.id));
        setMessages(mapped);
      }

      const lastSynced = await chatCache.getLastSyncedAt(authSlice.userId, conversationId);
      try {
        const params = lastSynced ? { after: lastSynced } : { limit: 50 };
        const res = await axiosInstance.get(`/chat/conversations/${conversationId}/messages`, { params });
        const fresh: ChatMessage[] = res.data?.messages || [];
        const serverClearedAt: string | null = res.data?.cleared_at ?? null;

        // Kalau ada "Clear Chat" yang terjadi di device lain sejak device ini terakhir
        // sync, cache pesan lokal di device ini bisa berisi pesan yang seharusnya sudah
        // hilang — buang cache lama itu dulu sebelum menampilkan apa pun.
        const wiped = await chatCache.reconcileClearedAt(authSlice.userId, conversationId, serverClearedAt);
        if (!cancelled && wiped) {
          messageIdsRef.current.clear();
          setMessages([]);
        }

        if (!cancelled && fresh.length) {
          fresh.forEach((m) => appendIfNew(m));
          await chatCache.saveMessages(authSlice.userId, conversationId, fresh);
        }
        if (!cancelled) setHasMoreOlder(!wiped && (cached.length > 0 || fresh.length === 50));
        markReadAndNotify(conversationId);
      } catch (err) {
        if (!cancelled) setErrorMsg(tc?.errorLoad || 'Gagal memuat percakapan');
      } finally {
        if (!cancelled) setLoadingInitial(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, authSlice.userId]);

  // Fallback: kalau title header kosong (mis. halaman di-refresh dan state React Router hilang),
  // ambil info percakapan dari server alih-alih membiarkan header kosong.
  useEffect(() => {
    if (headerTitle || !conversationId) return;
    axiosInstance.get('/chat/conversations')
      .then((res) => {
        const conv = (res.data || []).find((c: any) => c.id === conversationId);
        if (conv) {
          setHeaderTitle(conv.title);
          setConversationType(conv.type);
          setHeaderPhoto(conv.photo);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Scroll ke bawah + autofocus input saat halaman chat pertama kali selesai dimuat
  useEffect(() => {
    if (!loadingInitial) {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      messageInputRef.current?.focus();
    }
  }, [loadingInitial]);

  // Real-time via socket
  useEffect(() => {
    if (!conversationId) return;
    const socketUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';
    socketService.connect(socketUrl, authSlice.accessToken || undefined);

    const handleNewMessage = (payload: { conversation_id: number; message: ChatMessage }) => {
      if (payload.conversation_id !== conversationId) return;
      appendIfNew(payload.message);
      chatCache.saveMessages(authSlice.userId, conversationId, [payload.message]);
      if (String(payload.message.sender_id) !== String(authSlice.userId)) {
        markReadAndNotify(conversationId);
      }
      requestAnimationFrame(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      });
    };
    socketService.on('new_chat_message', handleNewMessage);

    // Device lain (atau device ini sendiri) baru saja "Clear Chat" — reconcile langsung
    // tanpa perlu tunggu buka-tutup layar ini.
    const handleChatCleared = (payload: { conversation_id: number; cleared_at: string | null }) => {
      if (payload.conversation_id !== conversationId) return;
      chatCache.reconcileClearedAt(authSlice.userId, conversationId, payload.cleared_at).then((wiped) => {
        if (wiped) {
          messageIdsRef.current.clear();
          setMessages([]);
        }
      });
    };
    socketService.on('chat_cleared', handleChatCleared);

    const handleMessageEdited = (payload: { conversation_id: number; message: ChatMessage }) => {
      if (payload.conversation_id !== conversationId) return;
      upsertMessage(payload.message);
      chatCache.saveMessages(authSlice.userId, conversationId, [payload.message]);
    };
    socketService.on('chat_message_edited', handleMessageEdited);

    const handleMessageDeleted = (payload: { conversation_id: number; message_id: number }) => {
      if (payload.conversation_id !== conversationId) return;
      markMessageDeleted(payload.message_id);
    };
    socketService.on('chat_message_deleted', handleMessageDeleted);

    return () => {
      socketService.off('new_chat_message', handleNewMessage);
      socketService.off('chat_cleared', handleChatCleared);
      socketService.off('chat_message_edited', handleMessageEdited);
      socketService.off('chat_message_deleted', handleMessageDeleted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, authSlice.accessToken, authSlice.userId]);

  // Long-press (touch & mouse) di atas bubble chat → buka menu Reply/Edit/Delete.
  const LONG_PRESS_MS = 450;
  const startLongPress = (msg: ChatMessage) => {
    longPressMovedRef.current = false;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      if (!longPressMovedRef.current) setContextMenuMsg(msg);
    }, LONG_PRESS_MS);
  };
  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  const onLongPressMove = () => {
    longPressMovedRef.current = true;
    cancelLongPress();
  };

  const loadOlder = async () => {
    if (loadingOlder || !hasMoreOlder || !messages.length) return;
    setLoadingOlder(true);
    const oldest = messages[0].created_at;
    try {
      const res = await axiosInstance.get(`/chat/conversations/${conversationId}/messages`, { params: { before: oldest, limit: 50 } });
      const older: ChatMessage[] = res.data?.messages || [];
      if (older.length) {
        older.forEach((m) => messageIdsRef.current.add(m.id));
        setMessages((prev) => [...older, ...prev]);
        await chatCache.saveMessages(authSlice.userId, conversationId, older);
      }
      setHasMoreOlder(older.length === 50);
    } catch {
      // silent — infinite scroll retry on next scroll event
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleScroll = () => {
    if (listRef.current && listRef.current.scrollTop < 60) loadOlder();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setErrorMsg(tc?.attachmentTooLarge || 'Ukuran file maksimal 7MB');
      return;
    }
    setErrorMsg('');
    const compressed = await compressImage(file);
    setAttachment(compressed);
  };

  const handleSend = async () => {
    if (sending) return;
    if (!messageText.trim() && !attachment) return;
    setSending(true);
    setErrorMsg('');
    try {
      if (editingMessage) {
        const res = await axiosInstance.patch(`/chat/conversations/${conversationId}/messages/${editingMessage.id}`, { body: messageText.trim() });
        const msg: ChatMessage = res.data;
        upsertMessage(msg);
        await chatCache.saveMessages(authSlice.userId, conversationId, [msg]);
        setEditingMessage(null);
      } else {
        const formData = new FormData();
        if (messageText.trim()) formData.append('body', messageText.trim());
        if (attachment) formData.append('attachment', attachment);
        if (replyingTo) formData.append('reply_to_message_id', String(replyingTo.id));
        const res = await axiosInstance.post(`/chat/conversations/${conversationId}/messages`, formData);
        const msg: ChatMessage = res.data;
        appendIfNew(msg);
        await chatCache.saveMessages(authSlice.userId, conversationId, [msg]);
        setAttachment(null);
        setReplyingTo(null);
        requestAnimationFrame(() => {
          if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
        });
      }
      setMessageText('');
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || tc?.errorSend || 'Gagal mengirim pesan');
    } finally {
      setSending(false);
    }
  };

  const closeContextMenu = () => setContextMenuMsg(null);

  const handleReply = (msg: ChatMessage) => {
    setEditingMessage(null);
    setReplyingTo(msg);
    closeContextMenu();
    requestAnimationFrame(() => messageInputRef.current?.focus());
  };

  const handleEditStart = (msg: ChatMessage) => {
    setReplyingTo(null);
    setEditingMessage(msg);
    setMessageText(msg.body || '');
    closeContextMenu();
    requestAnimationFrame(() => messageInputRef.current?.focus());
  };

  const cancelCompose = () => {
    setReplyingTo(null);
    setEditingMessage(null);
    setMessageText('');
  };

  const handleDeleteMessage = async (msg: ChatMessage) => {
    closeContextMenu();
    if (!window.confirm(tc?.deleteMessageConfirm || 'Hapus pesan ini?')) return;
    try {
      await axiosInstance.delete(`/chat/conversations/${conversationId}/messages/${msg.id}`);
      markMessageDeleted(msg.id);
      const cached = await chatCache.getMessages(authSlice.userId, conversationId);
      const updated = cached.find((c) => c.server_id === msg.id);
      if (updated) {
        await chatCache.saveMessages(authSlice.userId, conversationId, [{
          ...msg, body: null, attachment_url: null, attachment_name: null, attachment_type: null, deleted_at: new Date().toISOString(),
        }]);
      }
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || tc?.errorLoad || 'Gagal menghapus pesan');
    }
  };

  const openParticipants = async () => {
    setShowMenu(false);
    setShowParticipants(true);
    try {
      const res = await axiosInstance.get(`/chat/conversations/${conversationId}/participants`);
      setParticipants(res.data || []);
    } catch {}
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm(tc?.leaveGroupConfirm || 'Yakin ingin keluar dari grup ini?')) return;
    try {
      await axiosInstance.delete(`/chat/conversations/${conversationId}/participants/me`);
      navigate(-1);
    } catch {}
  };

  const handleClearChat = async () => {
    setShowMenu(false);
    if (!window.confirm(tc?.clearChatConfirm || 'Hapus riwayat chat ini? Hanya akan hilang untuk Anda, tidak untuk orang lain.')) return;
    try {
      const res = await axiosInstance.post(`/chat/conversations/${conversationId}/clear`);
      await chatCache.clearMessagesForConversation(authSlice.userId, conversationId);
      await chatCache.reconcileClearedAt(authSlice.userId, conversationId, res.data.cleared_at);
      messageIdsRef.current.clear();
      setMessages([]);
      setHasMoreOlder(false);
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || tc?.errorLoad || 'Gagal menghapus riwayat chat');
    }
  };

  const openSearch = () => {
    setShowMenu(false);
    setShowSearch(true);
    setSearchQuery('');
    setSearchResults(null);
  };

  const closeSearch = () => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleSearch = async (q: string) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const res = await axiosInstance.get(`/chat/conversations/${conversationId}/messages/search`, { params: { q: q.trim() } });
      setSearchResults(res.data || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const scrollToMessage = (id: number) => {
    requestAnimationFrame(() => {
      const el = listRef.current?.querySelector(`[data-message-id="${id}"]`);
      el?.scrollIntoView({ block: 'center', behavior: 'auto' });
    });
    setHighlightedMessageId(id);
    setTimeout(() => setHighlightedMessageId((cur) => (cur === id ? null : cur)), 2000);
  };

  // Klik hasil search → lompat ke pesan itu di percakapan utama. Kalau pesannya sudah
  // ada di window yang sedang di-load, tinggal scroll; kalau tidak (riwayat lama yang
  // belum di-load), ambil dulu potongan riwayat di sekitar pesan itu dari server.
  const jumpToMessage = async (target: ChatMessage) => {
    closeSearch();
    if (messageIdsRef.current.has(target.id)) {
      scrollToMessage(target.id);
      return;
    }

    setJumping(true);
    try {
      const [beforeRes, afterRes] = await Promise.all([
        axiosInstance.get(`/chat/conversations/${conversationId}/messages`, { params: { before: target.created_at, limit: 30 } }),
        axiosInstance.get(`/chat/conversations/${conversationId}/messages`, { params: { after: target.created_at, limit: 30 } }),
      ]);
      const before: ChatMessage[] = beforeRes.data?.messages || [];
      const after: ChatMessage[] = afterRes.data?.messages || [];
      const windowMessages = [...before, target, ...after].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );

      messageIdsRef.current = new Set(windowMessages.map((m) => m.id));
      setMessages(windowMessages);
      setHasMoreOlder(before.length === 30);
      await chatCache.saveMessages(authSlice.userId, conversationId, windowMessages);
      scrollToMessage(target.id);
    } catch {
      setErrorMsg(tc?.errorLoad || 'Gagal memuat pesan');
    } finally {
      setJumping(false);
    }
  };

  const bubbleStyle = (mine: boolean, highlighted: boolean): React.CSSProperties => ({
    maxWidth: '75%',
    alignSelf: mine ? 'flex-end' : 'flex-start',
    backgroundColor: highlighted ? '#FEF3C7' : (mine ? theme.colors.mainColor : '#fff'),
    color: highlighted ? theme.colors.mainDark : (mine ? '#fff' : theme.colors.mainDark),
    borderRadius: 14,
    padding: '8px 12px',
    marginBottom: 8,
    border: highlighted ? '1px solid #F59E0B' : (mine ? 'none' : '1px solid #E5E7EB'),
    transition: 'background-color 0.3s ease, border-color 0.3s ease',
  });

  if (!conversationId) {
    return (
      <div id="screen" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
        <components.Header title={tc?.listTitle || 'Chat'} goBack={true} />
        <main className="container" style={{ marginTop: 60, padding: 20 }}>
          <text.T14 style={{ color: '#6B7280' }}>{tc?.errorLoad || 'Percakapan tidak ditemukan'}</text.T14>
        </main>
      </div>
    );
  }

  return (
    <div id="screen" style={{ backgroundColor: '#F3F4F6', minHeight: '100vh' }}>
      <components.Header
        title={headerTitle}
        goBack={true}
        rightContent={
          <div style={{ position: 'relative' }}>
            <div onClick={() => setShowMenu((v) => !v)} style={{ padding: '0 16px', cursor: 'pointer' }}>
              <MoreIcon size={22} color={theme.colors.mainColor} />
            </div>
            {showMenu && (
              <>
                <div onClick={() => setShowMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 4090 }} />
                <div style={{
                  position: 'absolute', top: '100%', right: 8, backgroundColor: '#fff', borderRadius: 10,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)', minWidth: 170, overflow: 'hidden', zIndex: 4100,
                }}>
                  {conversationType === 'group' && (
                    <div onClick={openParticipants} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer' }}>
                      <InfoIcon size={18} color="#374151" />
                      <text.T14 style={{ color: theme.colors.mainDark }}>{tc?.groupInfo || 'Info Grup'}</text.T14>
                    </div>
                  )}
                  <div onClick={openSearch} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer', borderTop: conversationType === 'group' ? '1px solid #F3F4F6' : 'none' }}>
                    <SearchIcon size={18} color="#374151" />
                    <text.T14 style={{ color: theme.colors.mainDark }}>{tc?.searchInChat || 'Cari'}</text.T14>
                  </div>
                  <div onClick={handleClearChat} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer', borderTop: '1px solid #F3F4F6' }}>
                    <ClearIcon size={18} color="#DC2626" />
                    <text.T14 style={{ color: '#DC2626' }}>{tc?.clearChat || 'Hapus Chat'}</text.T14>
                  </div>
                </div>
              </>
            )}
          </div>
        }
      />

      <main
        ref={listRef}
        onScroll={handleScroll}
        className="container"
        style={{
          marginTop: 60, paddingTop: 12, paddingBottom: 90,
          height: 'calc(100vh - 60px)', overflowY: 'auto',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {(loadingOlder || jumping) && (
          <div style={{ textAlign: 'center', padding: 8 }}>
            <text.T12 style={{ color: '#9CA3AF' }}>{t.common.loading || 'Memuat...'}</text.T12>
          </div>
        )}

        {loadingInitial ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <text.T14 style={{ color: '#6B7280' }}>{t.common.loading || 'Memuat...'}</text.T14>
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <text.T14 style={{ color: '#9CA3AF' }}>{tc?.noMessagesYet || 'Belum ada pesan. Mulai percakapan!'}</text.T14>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const mine = String(msg.sender_id) === String(authSlice.userId);
            const fileUrl = msg.attachment_url ? `${imageBaseUrl}${msg.attachment_url}` : null;
            const showDateSeparator = idx === 0 || dayKey(msg.created_at) !== dayKey(messages[idx - 1].created_at);
            return (
              <React.Fragment key={msg.id}>
                {showDateSeparator && (
                  <div style={{ textAlign: 'center', margin: '10px 0' }}>
                    <span style={{ backgroundColor: '#E5E7EB', color: '#6B7280', fontSize: 11, padding: '4px 12px', borderRadius: 12 }}>
                      {formatDateSeparator(msg.created_at, tc?.today || 'Hari Ini', tc?.yesterday || 'Kemarin')}
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div
                  data-message-id={msg.id}
                  style={{ ...bubbleStyle(mine, msg.id === highlightedMessageId), cursor: 'pointer', userSelect: 'none' }}
                  onTouchStart={() => startLongPress(msg)}
                  onTouchMove={onLongPressMove}
                  onTouchEnd={cancelLongPress}
                  onMouseDown={() => startLongPress(msg)}
                  onMouseUp={cancelLongPress}
                  onMouseLeave={cancelLongPress}
                  onContextMenu={(e) => { e.preventDefault(); setContextMenuMsg(msg); }}
                >
                  {conversationType === 'group' && !mine && !msg.deleted_at && (
                    <text.T10 style={{ color: theme.colors.mainColor, fontWeight: '700', display: 'block', marginBottom: 2 }}>
                      {msg.sender_name || ''}
                    </text.T10>
                  )}
                  {msg.deleted_at ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <BlockIcon size={14} color={mine ? 'rgba(255,255,255,0.7)' : '#9CA3AF'} />
                      <text.T14 style={{ color: 'inherit', fontStyle: 'italic', opacity: 0.75 }}>
                        {tc?.messageDeleted || 'Pesan ini telah dihapus'}
                      </text.T14>
                    </div>
                  ) : (
                    <>
                      {msg.reply_to && (
                        <div
                          onClick={(e) => { e.stopPropagation(); if (msg.reply_to && !msg.reply_to.deleted) jumpToMessage(msg.reply_to as any); }}
                          style={{
                            borderLeft: `3px solid ${mine ? 'rgba(255,255,255,0.6)' : theme.colors.mainColor}`,
                            backgroundColor: mine ? 'rgba(255,255,255,0.12)' : '#F3F4F6',
                            borderRadius: 6, padding: '4px 8px', marginBottom: 6,
                          }}
                        >
                          <text.T10 style={{ color: 'inherit', fontWeight: '700', display: 'block', opacity: 0.9 }}>
                            {msg.reply_to.sender_name || ''}
                          </text.T10>
                          <text.T12 style={{ color: 'inherit', opacity: 0.8, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {msg.reply_to.deleted
                              ? (tc?.messageDeleted || 'Pesan ini telah dihapus')
                              : (msg.reply_to.body || (msg.reply_to.attachment_name ? `📎 ${msg.reply_to.attachment_name}` : ''))}
                          </text.T12>
                        </div>
                      )}
                      {msg.body && <text.T14 style={{ color: 'inherit' }}>{msg.body}</text.T14>}
                      {fileUrl && (
                        <div style={{ marginTop: msg.body ? 6 : 0 }}>
                          <AttachmentViewer items={[{ url: fileUrl, name: msg.attachment_name || 'file' }]} thumbnailSize={140} />
                        </div>
                      )}
                    </>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, marginTop: 4 }}>
                    {!!msg.edited_at && !msg.deleted_at && (
                      <text.T10 style={{ color: mine ? 'rgba(255,255,255,0.75)' : '#9CA3AF' }}>{tc?.edited || 'diedit'}</text.T10>
                    )}
                    <text.T10 style={{ color: mine ? 'rgba(255,255,255,0.75)' : '#9CA3AF' }}>
                      {new Date(msg.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                    </text.T10>
                  </div>
                </div>
                </div>
              </React.Fragment>
            );
          })
        )}
      </main>

      {errorMsg && (
        <div style={{ position: 'fixed', bottom: 70, left: 16, right: 16, maxWidth: 650, margin: '0 auto', backgroundColor: '#FEE2E2', borderRadius: 8, padding: '8px 12px' }}>
          <text.T12 style={{ color: '#DC2626' }}>{errorMsg}</text.T12>
        </div>
      )}

      {/* Input bar */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 650, backgroundColor: '#fff', borderTop: '1px solid #F3F4F6', padding: 10 }}>
          {(replyingTo || editingMessage) && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#F3F4F6',
              borderLeft: `3px solid ${theme.colors.mainColor}`, borderRadius: 6, padding: '6px 10px', marginBottom: 8,
            }}>
              {editingMessage ? <EditIcon size={16} color={theme.colors.mainColor} /> : <ReplyIcon size={16} color={theme.colors.mainColor} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <text.T10 style={{ color: theme.colors.mainColor, fontWeight: '700', display: 'block' }}>
                  {editingMessage ? (tc?.editingMessage || 'Edit pesan') : (tc?.replyingTo || 'Membalas')}
                  {replyingTo && !editingMessage ? ` ${replyingTo.sender_name || ''}` : ''}
                </text.T10>
                <text.T12 style={{ color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                  {(replyingTo || editingMessage)?.body || ((replyingTo || editingMessage)?.attachment_name ? `📎 ${(replyingTo || editingMessage)?.attachment_name}` : '')}
                </text.T12>
              </div>
              <div onClick={cancelCompose} style={{ cursor: 'pointer' }}>
                <CloseIcon size={16} color="#6B7280" />
              </div>
            </div>
          )}
          {attachment && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#F3F4F6', borderRadius: 8, padding: '6px 10px', marginBottom: 8 }}>
              <text.T12 style={{ color: theme.colors.mainDark, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {attachment.name}
              </text.T12>
              <div onClick={() => setAttachment(null)} style={{ cursor: 'pointer' }}>
                <CloseIcon size={16} color="#6B7280" />
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_ATTACHMENT_ACCEPT}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <div onClick={() => fileInputRef.current?.click()} style={{ cursor: 'pointer', padding: 8, flexShrink: 0 }}>
              <AttachIcon size={22} color={theme.colors.mainColor} />
            </div>
            <input
              ref={messageInputRef}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
              placeholder={tc?.messagePlaceholder || 'Tulis pesan...'}
              style={{
                flex: 1, border: '1px solid #D1D5DB', borderRadius: 20, padding: '9px 14px',
                fontSize: 14, outline: 'none', fontFamily: 'inherit', color: theme.colors.mainDark,
              }}
            />
            <div
              onClick={sending ? undefined : handleSend}
              style={{
                width: 38, height: 38, borderRadius: 19, backgroundColor: theme.colors.mainColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.6 : 1, flexShrink: 0,
              }}
            >
              <SendIcon size={18} color="#fff" />
            </div>
          </div>
        </div>
      </div>

      {/* Context menu pesan: Reply / Edit / Delete */}
      {contextMenuMsg && (
        <div
          onClick={closeContextMenu}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 4000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 650, backgroundColor: '#fff', borderRadius: '16px 16px 0 0', padding: 8, paddingBottom: 20 }}
          >
            <div onClick={() => handleReply(contextMenuMsg)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', cursor: 'pointer' }}>
              <ReplyIcon size={20} color="#374151" />
              <text.T14 style={{ color: theme.colors.mainDark }}>{tc?.reply || 'Balas'}</text.T14>
            </div>
            {String(contextMenuMsg.sender_id) === String(authSlice.userId) && !contextMenuMsg.deleted_at && (
              <>
                <div onClick={() => handleEditStart(contextMenuMsg)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', cursor: 'pointer' }}>
                  <EditIcon size={20} color="#374151" />
                  <text.T14 style={{ color: theme.colors.mainDark }}>{tc?.edit || 'Edit'}</text.T14>
                </div>
                <div onClick={() => handleDeleteMessage(contextMenuMsg)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', cursor: 'pointer' }}>
                  <DeleteIcon size={20} color="#DC2626" />
                  <text.T14 style={{ color: '#DC2626' }}>{tc?.delete || 'Hapus'}</text.T14>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Participants sheet */}
      {showParticipants && (
        <div
          onClick={() => setShowParticipants(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 4000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 650, backgroundColor: '#fff', borderRadius: '16px 16px 0 0', padding: 16, maxHeight: '70vh', overflowY: 'auto' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <text.T16 style={{ fontWeight: '700', color: theme.colors.mainDark }}>{tc?.participants || 'Anggota'}</text.T16>
              <div onClick={() => setShowParticipants(false)} style={{ cursor: 'pointer' }}><CloseIcon size={22} color="#6B7280" /></div>
            </div>
            {participants.map((p) => (
              <div key={p.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                <div style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {p.profile_picture ? (
                    <img src={`${imageBaseUrl}/${p.profile_picture}`} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <PersonIcon size={18} color="#9CA3AF" />
                  )}
                </div>
                <text.T14 style={{ color: theme.colors.mainDark, flex: 1 }}>
                  {p.name}{String(p.user_id) === String(authSlice.userId) ? ` (${tc?.you || 'Anda'})` : ''}
                </text.T14>
                {p.role === 'admin' && <text.T10 style={{ color: theme.colors.mainColor }}>{tc?.groupAdmin || 'Admin'}</text.T10>}
              </div>
            ))}
            <div
              onClick={handleLeaveGroup}
              style={{ marginTop: 14, textAlign: 'center', padding: 12, borderRadius: 10, backgroundColor: '#FEE2E2', cursor: 'pointer' }}
            >
              <text.T14 style={{ color: '#DC2626', fontWeight: '600' }}>{tc?.leaveGroup || 'Keluar dari Grup'}</text.T14>
            </div>
          </div>
        </div>
      )}

      {/* Search overlay — cari di percakapan ini saja */}
      {showSearch && (
        <div style={{ position: 'fixed', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 650, backgroundColor: '#fff', zIndex: 5000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
            <div onClick={closeSearch} style={{ cursor: 'pointer', padding: 4 }}>
              <CloseIcon size={22} color="#6B7280" />
            </div>
            <input
              autoFocus
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={tc?.searchInChatPlaceholder || 'Cari di percakapan ini...'}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, fontFamily: 'inherit', color: theme.colors.mainDark }}
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
            {searching ? (
              <div style={{ textAlign: 'center', padding: 30 }}>
                <text.T14 style={{ color: '#6B7280' }}>{t.common.loading || 'Memuat...'}</text.T14>
              </div>
            ) : searchResults === null ? null : searchResults.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 30 }}>
                <text.T14 style={{ color: '#9CA3AF' }}>{tc?.noSearchResults || 'Tidak ada pesan ditemukan'}</text.T14>
              </div>
            ) : (
              searchResults.map((m) => (
                <div
                  key={m.id}
                  onClick={() => jumpToMessage(m)}
                  style={{ backgroundColor: '#F9FAFB', borderRadius: 10, padding: '10px 14px', marginBottom: 8, cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <text.T12 style={{ color: theme.colors.mainColor, fontWeight: '700' }}>
                      {String(m.sender_id) === String(authSlice.userId) ? (tc?.you || 'Anda') : (m.sender_name || '')}
                    </text.T12>
                    <text.T10 style={{ color: '#9CA3AF' }}>
                      {new Date(m.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </text.T10>
                  </div>
                  <text.T14 style={{ color: theme.colors.mainDark }}>{m.body}</text.T14>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
